import type { TableCellKey } from '../../../../stores/tableCellStore';
import type { TableCellLayoutAlgorithmId } from './tableSchema.types';

export type TableCellLayoutConfig = {
  algorithmId: TableCellLayoutAlgorithmId;
  params?: Record<string, unknown>;
};

export type TableCellLayoutContext = {
  cellRect: { x: number; y: number; w: number; h: number };
  items: Array<{ id: string; w: number; h: number; x: number; y: number }>;
  params?: Record<string, unknown>;
};

export interface ITableCellLayoutAlgorithm {
  id: TableCellLayoutAlgorithmId;
  label: string;
  arrange: (ctx: TableCellLayoutContext) => Record<string, { x: number; y: number }>;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const fixedAlgorithm: ITableCellLayoutAlgorithm = {
  id: 'fixed',
  label: 'Fixed',
  arrange: (ctx) => {
    const out: Record<string, { x: number; y: number }> = {};
    const { cellRect } = ctx;
    for (const it of ctx.items) {
      // For fixed layout, ctx.items.x/y are treated as offsets from cellRect top-left.
      const x = clamp(cellRect.x + it.x, cellRect.x, cellRect.x + cellRect.w - it.w);
      const y = clamp(cellRect.y + it.y, cellRect.y, cellRect.y + cellRect.h - it.h);
      out[it.id] = { x, y };
    }
    return out;
  },
};

const gridAlgorithm: ITableCellLayoutAlgorithm = {
  id: 'grid',
  label: 'Grid',
  arrange: (ctx) => {
    const out: Record<string, { x: number; y: number }> = {};
    const gap = typeof (ctx.params as any)?.gap === 'number' ? Math.max(0, (ctx.params as any).gap) : 8;
    const padding = typeof (ctx.params as any)?.padding === 'number' ? Math.max(0, (ctx.params as any).padding) : 8;
    const columnsParam = (ctx.params as any)?.columns;

    const maxW = Math.max(...ctx.items.map((i) => i.w), 60);
    const availableW = Math.max(1, ctx.cellRect.w - padding * 2);
    const autoCols = Math.max(1, Math.floor((availableW + gap) / (maxW + gap)));
    const cols = typeof columnsParam === 'number' && Number.isFinite(columnsParam) ? Math.max(1, Math.floor(columnsParam)) : autoCols;

    ctx.items.forEach((it, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = ctx.cellRect.x + padding + col * (maxW + gap);
      const y = ctx.cellRect.y + padding + row * (it.h + gap);
      out[it.id] = {
        x: clamp(x, ctx.cellRect.x, ctx.cellRect.x + ctx.cellRect.w - it.w),
        y: clamp(y, ctx.cellRect.y, ctx.cellRect.y + ctx.cellRect.h - it.h),
      };
    });

    return out;
  },
};

const REGISTRY: Record<TableCellLayoutAlgorithmId, ITableCellLayoutAlgorithm> = {
  fixed: fixedAlgorithm,
  grid: gridAlgorithm,
};

export const getTableCellLayoutAlgorithm = (id: TableCellLayoutAlgorithmId): ITableCellLayoutAlgorithm => {
  return REGISTRY[id];
};

export const resolveCellLayoutConfig = (
  tableAttrs: Record<string, unknown>,
  cellKey: TableCellKey,
): TableCellLayoutConfig => {
  const map = (tableAttrs as any).cellLayouts as Record<string, TableCellLayoutConfig> | undefined;
  const config = map?.[cellKey] as TableCellLayoutConfig | undefined;
  if (config && (config.algorithmId === 'fixed' || config.algorithmId === 'grid')) {
    return { algorithmId: config.algorithmId, params: config.params };
  }
  return { algorithmId: 'fixed' };
};

